import React, { useState } from "react";
import { useLocation, useNavigate, Link } from "react-router-dom";
import Logo from "../../assets/images/disktop-logo.png";
import NotificationDropdown from "./NotificationDropdown";
import UserMenu from "./UserMenu";
import { useAuthStore } from "../../store/useAuthStore";

const Header = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  // Get auth state
  const { token, role } = useAuthStore();
  const isLoggedIn = !!token;
  const normalizedRole = role?.toLowerCase();

  const isAuthPage =
    location.pathname === "/signin" ||
    location.pathname === "/signup" ||
    location.pathname === "/vendor/onboarding";

  const isOtpResetForgotPage =
    location.pathname.includes("otp") ||
    location.pathname.includes("reset") ||
    location.pathname.includes("forgot");

  const mockNotifications = [
    { message: "New booking received", time: "2 hours ago" },
    { message: "Payment processed", time: "5 hours ago" },
  ];

  // User navigation links
  const userNavLinks = [
    { label: "Home", path: "/user/home" },
    { label: "Bookings", path: "/user/my-bookings" },
    { label: "Explore", path: "/user/experience-detail/" },
    { label: "Support", path: "/user/support" },
  ];

  // Get profile paths based on role
  const getProfilePath = () => {
    if (normalizedRole === "vendor") return "/vendor/profile";
    if (normalizedRole === "admin") return "/admin/dashboard";
    return "/user/profile";
  };

  const getSettingsPath = () => {
    if (normalizedRole === "vendor") return "/vendor/profile/edit";
    if (normalizedRole === "admin") return "/admin/dashboard";
    return "/user/profile/edit";
  };

  const profilePath = getProfilePath();
  const settingsPath = getSettingsPath();

  return (
    <header className="w-full">
      {/* ================= DESKTOP HEADER ================= */}
      <div className="hidden lg:block">
        <nav className="w-full py-4 flex items-center lg:px-5">
          {/* Left - Logo */}
          <div
            className="cursor-pointer flex-shrink-0"
            onClick={() => navigate("/")}
          >
            <img className="w-[65px] mt-[-12px]" src={Logo} alt="logo" />
          </div>

          {!isAuthPage && !isOtpResetForgotPage && (
            <>
              {/* Center - Navigation */}
              <ul className="flex-1 flex justify-center items-center gap-3 xl:gap-6 lg:text-[16px] xl:text-[18px] text-[#000000]">
                {/* 👇 Guest user ke liye Experiences */}
                {!isLoggedIn && (
                  <Link
                    to="/experiences"
                    className={`hover:text-[#1FADC6] transition ${
                      location.pathname === "/experiences"
                        ? "text-[#1FADC6] font-semibold text-[22px]"
                        : "font-semibold text-[22px]"
                    }`}
                  >
                    <li>Explore</li>
                  </Link>
                )}

                {normalizedRole === "user" &&
                  userNavLinks.map((link) => (
                    <Link
                      key={link.path}
                      to={link.path}
                      className={`hover:text-[#1FADC6] transition ${
                        location.pathname === link.path
                          ? "text-[#1FADC6] font-semibold"
                          : ""
                      }`}
                    >
                      <li>{link.label}</li>
                    </Link>
                  ))}
              </ul>

              {/* Right - Auth / User */}
              <div className="flex items-center gap-4 flex-shrink-0">
                {isLoggedIn ? (
                  <UserMenu
                    onProfile={() => navigate(profilePath)}
                    onSettings={() => navigate(settingsPath)}
                  />
                ) : (
                  <div className="flex items-center gap-6">
                    <Link
                      to="/signin"
                      className="font-semibold text-lg hover:text-[#1FADC6]"
                    >
                      Login
                    </Link>
                    <Link
                      to="/signup"
                      className="font-semibold text-lg border border-[#1FADC6] text-[#1FADC6] rounded-md py-2 px-8 hover:bg-[#1FADC6] hover:text-white transition"
                    >
                      Sign Up
                    </Link>
                  </div>
                )}
              </div>
            </>
          )}
        </nav>
      </div>

      {/* ================= MOBILE TOP HEADER (NO LOGO) ================= */}
      <div className="block lg:hidden">
        <div className="flex items-center justify-between py-3 px-3 sm:px-4">
          {!isAuthPage && !isOtpResetForgotPage ? (
            <button
              type="button"
              className="p-2 -ml-1"
              onClick={() => setOpen(true)}
              aria-label="Open menu"
            >
              <svg width="24" height="20" viewBox="0 0 24 20">
                <line
                  x1="1.5"
                  y1="1.5"
                  x2="22.5"
                  y2="1.5"
                  stroke="#6C7072"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <line
                  x1="1.5"
                  y1="9.5"
                  x2="22.5"
                  y2="9.5"
                  stroke="#6C7072"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
                <line
                  x1="1.5"
                  y1="17.5"
                  x2="22.5"
                  y2="17.5"
                  stroke="#6C7072"
                  strokeWidth="3"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          ) : (
            <div className="w-10" />
          )}

          <div className="flex-1" />

          {!isAuthPage && !isOtpResetForgotPage ? (
            <div className="flex items-center gap-2">
              {isLoggedIn ? (
                <UserMenu
                  onProfile={() => navigate(profilePath)}
                  onSettings={() => navigate(settingsPath)}
                />
              ) : (
                <>
                  <Link to="/signin">
                    <button className="bg-[#62D2E6] text-white text-[12px] py-2 px-3 rounded-md">
                      Login
                    </button>
                  </Link>
                  <Link to="/signup">
                    <button className="border border-[#62D2E6] text-[#62D2E6] text-[12px] py-2 px-3 rounded-md">
                      Sign Up
                    </button>
                  </Link>
                </>
              )}
            </div>
          ) : (
            <div className="w-10" />
          )}
        </div>
      </div>

      {/* ================= MOBILE SIDEBAR ================= */}
      <div
        className={`fixed top-0 left-0 h-full w-[260px] bg-white shadow-xl transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } transition-transform duration-300 z-50`}
      >
        <div className="flex items-center justify-between px-4 py-4 border-b">
          <img
            src={Logo}
            alt="logo"
            className="w-[140px] cursor-pointer"
            onClick={() => {
              navigate("/");
              setOpen(false);
            }}
          />
          <button onClick={() => setOpen(false)}>
            <svg width="26" height="26" viewBox="0 0 24 24">
              <path
                stroke="#000"
                strokeWidth="2"
                strokeLinecap="round"
                d="M6 6l12 12M18 6L6 18"
              />
            </svg>
          </button>
        </div>

        <ul className="flex flex-col gap-4 px-6 py-6 text-[18px] font-medium">
          {userNavLinks.map((link) => (
            <Link key={link.path} to={link.path} onClick={() => setOpen(false)}>
              <li>{link.label}</li>
            </Link>
          ))}
        </ul>
      </div>

      {open && (
        <div
          onClick={() => setOpen(false)}
          className="fixed inset-0 bg-black/50 z-40"
        />
      )}
    </header>
  );
};

export default Header;
